import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import Modal from '../../components/ui/Modal';
import { 
  CreditCard, 
  Calendar, 
  Download, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  XCircle,
  ExternalLink,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';

interface SubscriptionDetails {
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  planName: string;
  trialDaysRemaining: number | null;
}

interface Invoice {
  id: string;
  date: string;
  amount: number;
  status: string;
  invoicePdf: string | null;
  currency: string;
}

interface Plan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
}

const StripeBillingPage: React.FC = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);

  const isAdmin = user?.roleName === 'Admin';

  useEffect(() => {
    if (isAdmin) {
      loadSubscriptionData();
    }
  }, [isAdmin]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);

      const [subscriptionResult, invoicesResult] = await Promise.allSettled([
        apiService.getCurrentSubscription(),
        apiService.getSubscriptionInvoices()
      ]);

      if (subscriptionResult.status === 'fulfilled' && subscriptionResult.value) {
        const subscriptionData = subscriptionResult.value;
        const trialDaysRemaining = subscriptionData.trialEndDate
          ? Math.max(0, Math.ceil((new Date(subscriptionData.trialEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
          : null;

        setSubscription({
          status: subscriptionData.status,
          currentPeriodEnd: subscriptionData.currentPeriodEnd || null,
          cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd ?? false,
          planName: subscriptionData.planName,
          trialDaysRemaining
        });
      } else if (subscriptionResult.status === 'rejected') {
        console.error('Error loading subscription:', subscriptionResult.reason);
        toast.error('Failed to load subscription details');
      }

      if (invoicesResult.status === 'fulfilled' && invoicesResult.value.success && invoicesResult.value.data) {
        setInvoices(invoicesResult.value.data.map(inv => ({
          id: inv.id,
          date: inv.date.toString(),
          amount: inv.amount,
          status: inv.status,
          invoicePdf: inv.invoicePdf || null,
          currency: inv.currency
        })));
      } else if (invoicesResult.status === 'rejected') {
        console.error('Error loading invoices:', invoicesResult.reason);
        setInvoices([]);
        toast.error('Billing history is temporarily unavailable');
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradePlan = async (planId: string) => {
    try {
      setActionLoading(planId);

      // Use unified subscription checkout endpoint
      const response = await apiService.createStripeCheckoutSession(
        planId,
        `${window.location.origin}/settings/stripe-billing?success=true`,
        `${window.location.origin}/settings/stripe-billing?canceled=true`
      );

      // Redirect to Stripe checkout
      if (response.success && response.data.url) {
        console.log('Redirecting to Stripe checkout URL:', response.data.url);
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout process');
    } finally {
      setActionLoading(null);
    }
  };

  const handleManagePaymentMethod = async () => {
    try {
      setActionLoading('payment-method');

      // Use unified subscription portal endpoint
      const response = await apiService.createStripePortalSession(window.location.href);

      // Open Stripe portal in new tab to keep user connected to the app
      if (response.success && response.data.url) {
        window.open(response.data.url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Failed to open payment management');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading('cancel');

      // Use unified subscription cancel endpoint
      const response = await apiService.cancelStripeSubscription(cancelImmediately);

      if (response.success) {
        toast.success(response.data || (cancelImmediately ? 'Subscription cancelled immediately' : 'Subscription will be cancelled at the end of the current period'));
      } else {
        throw new Error(response.message || 'Failed to cancel subscription');
      }

      setShowCancelModal(false);
      loadSubscriptionData();
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      trialing: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      past_due: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      canceled: { color: 'bg-red-100 text-red-800', icon: XCircle },
      incomplete: { color: 'bg-gray-100 text-gray-800', icon: Clock }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.incomplete;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </Badge>
    );
  };

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600">Only administrators can manage billing and subscriptions.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid gap-6">
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Billing & Subscription</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your WorkOrder HQ subscription and billing information</p>
        </div>
      </div>

      {/* Current Subscription Section */}
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Current Subscription</h2>
          <div className="flex items-center gap-2">
            {subscription && getStatusBadge(subscription.status)}
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  // Simply reload the subscription data - no special "fix" endpoint needed
                  await loadSubscriptionData();
                  toast.success('Subscription refreshed successfully');
                  // Force page refresh to update banner
                  window.location.reload();
                } catch (error) {
                  toast.error('Failed to refresh subscription');
                }
              }}
            >
              Refresh Status
            </Button>
          </div>
        </div>

        {subscription && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-lg px-3 py-1">
                {subscription.planName}
              </Badge>
            </div>

            {subscription.status === 'trialing' && subscription.trialDaysRemaining !== null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Trial Progress</span>
                  <span className="font-medium">{subscription.trialDaysRemaining} days remaining</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-accent-color h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.max(0, (14 - subscription.trialDaysRemaining) / 14 * 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {subscription.currentPeriodEnd && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  Current period: {formatDate(subscription.currentPeriodEnd)}
                </span>
              </div>
            )}

            {subscription.cancelAtPeriodEnd && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Subscription will end on {subscription.currentPeriodEnd && formatDate(subscription.currentPeriodEnd)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Payment Method Section */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Method</h2>
        <p className="text-gray-600 mb-4">Manage your payment methods, billing address, and view invoices.</p>
        <Button 
          onClick={handleManagePaymentMethod}
          disabled={actionLoading === 'payment-method'}
          className="flex items-center gap-2"
        >
          {actionLoading === 'payment-method' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4" />
          )}
          Manage Payment Method
        </Button>
      </Card>

      {/* Billing History Section */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Billing History</h2>
        {invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 font-medium text-gray-900">Date</th>
                  <th className="text-left py-2 font-medium text-gray-900">Description</th>
                  <th className="text-left py-2 font-medium text-gray-900">Amount</th>
                  <th className="text-left py-2 font-medium text-gray-900">Status</th>
                  <th className="text-left py-2 font-medium text-gray-900">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100">
                    <td className="py-3 text-gray-600">{formatDate(invoice.date)}</td>
                    <td className="py-3 text-gray-900">Invoice {invoice.id}</td>
                    <td className="py-3 text-gray-900 font-medium">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </td>
                    <td className="py-3">
                      <Badge 
                        className={
                          invoice.status === 'succeeded' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }
                      >
                        {invoice.status}
                      </Badge>
                    </td>
                    <td className="py-3">
                      {invoice.invoicePdf ? (
                        <a 
                          href={invoice.invoicePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No billing history available</p>
        )}
      </Card>

      {/* Cancel Subscription Section */}
      {subscription && subscription.status === 'active' && !subscription.cancelAtPeriodEnd && (
        <Card className="p-6 border-red-200">
          <h2 className="text-xl font-semibold text-red-900 mb-4">Cancel Subscription</h2>
          <p className="text-gray-600 mb-4">
            Canceling your subscription will remove access to premium features. You can reactivate anytime.
          </p>
          <Button 
            variant="destructive"
            onClick={() => setShowCancelModal(true)}
            className="flex items-center gap-2"
          >
            <XCircle className="w-4 h-4" />
            Cancel Subscription
          </Button>
        </Card>
      )}

      {/* Reactivate Subscription Section */}
      {subscription && subscription.status === 'active' && subscription.cancelAtPeriodEnd && (
        <Card className="p-6 border-green-200">
          <h2 className="text-xl font-semibold text-green-900 mb-4">Reactivate Subscription</h2>
          <p className="text-gray-600 mb-4">
            Your subscription is scheduled to end. You can reactivate it anytime before the end of your current period.
          </p>
          <Button
            onClick={async () => {
              try {
                setActionLoading('reactivate');

                // Use unified subscription reactivate endpoint
                const response = await apiService.reactivateSubscription();

                if (response.success) {
                  toast.success(response.data || 'Subscription reactivated successfully');
                } else {
                  throw new Error(response.message || 'Failed to reactivate subscription');
                }

                await loadSubscriptionData();
              } catch (error: any) {
                console.error('Error reactivating subscription:', error);
                toast.error(error.message || 'Failed to reactivate subscription');
              } finally {
                setActionLoading(null);
              }
            }}
            disabled={actionLoading === 'reactivate'}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
          >
            {actionLoading === 'reactivate' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Reactivate Subscription
          </Button>
        </Card>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Warning</span>
            </div>
            <p className="text-red-700 text-sm">
              Canceling your subscription will remove access to premium features. 
              You will lose access to invoicing, reports, and other professional features.
            </p>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={cancelImmediately}
                onChange={(e) => setCancelImmediately(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                Cancel immediately (vs. cancel at period end)
              </span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={actionLoading === 'cancel'}
              className="flex items-center gap-2"
            >
              {actionLoading === 'cancel' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              {cancelImmediately ? 'Cancel Immediately' : 'Cancel at Period End'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
            >
              Keep Subscription
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StripeBillingPage;
